// Humanización de campos y valores del detail. Orquesta helpers ya existentes en la app
// (períodos, métricas) — acá no se inventa formateo nuevo.

import {getPeriodName} from '../../../../periods';
import {getMetricName} from '../../../dashboard/columns';
import {isAmount} from './shapes';
import {FIELD_LABELS, FIELD_VALUE_LABELS, VALUE_LABELS, PERCENT_FIELDS, MONEY_FIELDS, PERIOD_FIELDS, METRIC_FIELDS} from './labels';

// Strings largas (notas, URLs) que caen a un render genérico: se truncan para que no
// rompan el feed. El dato completo vive en la API.
const LONG_VALUE_MAX_CHARS = 160;

export function isNumeric(value) {
    if (typeof value === 'number') return Number.isFinite(value);
    return typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value));
}

// Números legibles: separador de miles + máximo 2 decimales (el backend guarda floats crudos
// tipo 39978.16000000002).
export function formatNumber(value) {
    return Number(value).toLocaleString('en-US', {maximumFractionDigits: 2});
}

// Un importe con dos decimales fijos y SIN símbolo: "1,000,935.00". Para cuando se sabe que es plata
// pero no en qué moneda (el ODS de una alerta vieja): mejor sin símbolo que con uno inventado.
export function formatAmount(value) {
    return Number(value).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
}

// Moneda "$ 1,000,935.00" (+ código ISO cuando se conoce). IDEMPOTENTE: los valores que el
// backend ya manda formateados como string ("$ 17,189,449.49") pasan tal cual — nunca
// re-parsear un string formateado.
export function formatMoney(value, currency) {
    if (!isNumeric(value)) return typeof value === 'string' ? value : '';
    const formatted = formatAmount(value);
    return currency ? `${formatted} ${currency}` : `$ ${formatted}`;
}

// Porcentaje con clamp visual ±999% (regla de goalFormatters.formatPercentage).
// `signed` fuerza el "+" en positivos (para deltas; los progresos van sin signo).
export function formatPercent(value, {signed = false} = {}) {
    if (!isNumeric(value)) return typeof value === 'string' ? value : '';
    const n = Number(value);
    if (n > 999) return '+999%';
    if (n < -999) return '-999%';
    const prefix = signed && n > 0 ? '+' : '';
    return `${prefix}${formatNumber(n)}%`;
}

// Duración en minutos → "45 min" / "2 h 15 min" / "3 días". El backend manda un entero (y no
// un timestamp) para que el dato no envejezca al releer la bitácora meses después.
// Devuelve null ante cualquier valor no usable: el llamador cae a la frase sin duración.
export function formatDurationMinutes(minutes, t) {
    if (!isNumeric(minutes)) return null;
    const total = Math.round(Number(minutes));
    if (total < 0) return null;
    if (total < 60) return t('activity_log.duration.minutes', {n: total});
    if (total < 1440) {
        const hours = Math.floor(total / 60);
        const rest = total % 60;
        if (rest === 0) return t('activity_log.duration.hours', {n: hours});
        return t('activity_log.duration.hours_minutes', {h: hours, m: rest});
    }
    // La key de plural se elige acá (convención del módulo, ver change_suffix_one/_other en
    // diff_field_types.js) en vez de delegar en la pluralización de i18next.
    const days = Math.floor(total / 1440);
    return t(days === 1 ? 'activity_log.duration.days_one' : 'activity_log.duration.days_other', {count: days});
}

// "snake_case_raro" → "Snake case raro" — último recurso para campos sin key i18n.
// Nunca mostrar el key crudo con guiones bajos.
export function prettifyKey(key) {
    const text = String(key).replace(/_/g, ' ').trim();
    if (!text) return String(key);
    return text.charAt(0).toUpperCase() + text.slice(1);
}

export function humanizeFieldLabel(field, t) {
    if (FIELD_LABELS[field]) return t(FIELD_LABELS[field]);
    return prettifyKey(field);
}

// Campos cuyo valor es una constante de canal ('GOOGLE_CHANNEL', 'ANALYTICS_CHANNEL'...).
export const CHANNEL_FIELDS = new Set(['campaign_channel', 'channel']);

// FACEBOOK es el nombre legacy de Meta; ANALYTICS es GA4. Un canal desconocido se
// prettifica (nunca se muestra el enum crudo).
const CHANNEL_VALUE_LABELS = {
    GOOGLE_CHANNEL: 'activity_log.channel_google',
    FACEBOOK_CHANNEL: 'activity_log.channel_meta',
    META_CHANNEL: 'activity_log.channel_meta',
    TIKTOK_CHANNEL: 'activity_log.channel_tiktok',
    LINKEDIN_CHANNEL: 'activity_log.channel_linkedin',
    ANALYTICS_CHANNEL: 'activity_log.channel_ga4',
    SHEET_CHANNEL: 'activity_log.channel_sheet',
    SHOPIFY_CHANNEL: 'activity_log.channel_shopify',
    TIENDA_NUBE_CHANNEL: 'activity_log.channel_tienda_nube',
    WOOCOMMERCE_CHANNEL: 'activity_log.channel_woocommerce',
    VTEX_CHANNEL: 'activity_log.channel_vtex',
};

export function humanizeChannel(value, t) {
    if (typeof value !== 'string' || !value.trim()) return null;
    const raw = value.trim().toUpperCase();
    const key = CHANNEL_VALUE_LABELS[raw] || CHANNEL_VALUE_LABELS[`${raw}_CHANNEL`];
    if (key) return t(key);
    if (raw.endsWith('_CHANNEL')) return prettifyKey(raw.replace(/_CHANNEL$/, '').toLowerCase());
    return null;
}

// Un período no canónico (enum futuro del backend) NO se muestra crudo: devolver null deja
// que cada renderer degrade (frase corta, stat omitido) en vez de imprimir LAST_45_DAYS_X.
export function humanizePeriod(value, t) {
    const name = getPeriodName(value, t);
    if (typeof name !== 'string') return null;
    if (name === value && /^[A-Z0-9_]+$/.test(String(value))) return null;
    return name;
}

// "CUSTOM:<id>" se resuelve contra el cache de display_names (columns.js); si la pantalla
// no lo hidrató, el fallback es un label genérico — jamás el ID crudo.
const CUSTOM_METRIC_PATTERN = /^CUSTOM:\d+$/;

export function humanizeMetricName(value, t) {
    const resolved = getMetricName(t, value);
    if (typeof resolved !== 'string') return null;
    if (CUSTOM_METRIC_PATTERN.test(resolved)) return t('activity_log.value.custom_metric');
    return resolved;
}

function truncateLongValue(text) {
    if (text.length <= LONG_VALUE_MAX_CHARS) return text;
    return `${text.slice(0, LONG_VALUE_MAX_CHARS).trimEnd()}…`;
}

// Un solo punto de verdad para humanizar un valor escalar según su campo.
// Devuelve string para todo lo representable; null para objetos complejos
// (esos los resuelve cada componente: listas de campañas, diffs anidados, etc.).
export function humanizeValue(field, value, t) {
    if (value === null || value === undefined || value === '') return t('activity_log.empty');
    if (typeof value === 'boolean') return value ? t('activity_log.value_true') : t('activity_log.value_false');
    // Los objetos se resuelven ACÁ, antes que cualquier passthrough (getPeriodName /
    // getMetricName devuelven el valor crudo cuando no lo conocen — con un objeto eso
    // atravesaría el error boundary como "Objects are not valid as a React child").
    if (typeof value === 'object') {
        if (isAmount(value)) {
            const amount = value.amount ?? '';
            if (amount === '') return t('activity_log.empty');
            return isNumeric(amount)
                ? formatMoney(amount, value.currency)
                : `${amount}${value.currency ? ` ${value.currency}` : ''}`;
        }
        return null;
    }
    if (PERIOD_FIELDS.has(field)) return humanizePeriod(value, t);
    if (METRIC_FIELDS.has(field)) return humanizeMetricName(value, t);
    if (CHANNEL_FIELDS.has(field)) {
        const channel = humanizeChannel(value, t);
        if (channel) return channel;
    }
    if (PERCENT_FIELDS.has(field) && isNumeric(value)) return formatPercent(value);
    if (MONEY_FIELDS.has(field) && isNumeric(value)) return formatMoney(value);
    const fieldValueKey = FIELD_VALUE_LABELS[field] && FIELD_VALUE_LABELS[field][value];
    if (fieldValueKey) return t(fieldValueKey);
    if (VALUE_LABELS[value]) return t(VALUE_LABELS[value]);
    if (isNumeric(value)) return formatNumber(value);
    return truncateLongValue(String(value));
}
